import { useState, type ReactNode } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import type { DesignerPath, PathSlotId } from '../types';

interface PathLegendProps {
  paths: DesignerPath[];
  activePathId: PathSlotId;
  onSelectPath: (id: PathSlotId) => void;
  onRenamePath: (id: PathSlotId, name: string) => void;
  onRemovePath: (id: PathSlotId) => void;
  onAddPath: () => void;
}

/**
 * The paths, one row each; the row is the way to pick which path the chart
 * edits. The row used to be almost all a text box, so a click on the name
 * started a rename instead of selecting, and the only sign of the selected
 * path was a 2px line. Now any click on the row selects it, the selected row is
 * filled and says "Editing", and renaming sits behind the pencil.
 */
export function PathLegend({
  paths,
  activePathId,
  onSelectPath,
  onRenamePath,
  onRemovePath,
  onAddPath,
}: PathLegendProps) {
  const [renaming, setRenaming] = useState<PathSlotId | null>(null);

  return (
    <>
      <div role="radiogroup" aria-label="Path to edit" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {paths.map((p) => {
          const active = p.id === activePathId;
          return (
            <div
              key={p.id}
              role="radio"
              aria-checked={active}
              tabIndex={0}
              onClick={() => onSelectPath(p.id)}
              onKeyDown={(e) => {
                if (renaming === p.id) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectPath(p.id);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 8px 8px 10px',
                background: active ? `${p.color}26` : 'var(--bg)',
                border: `1px solid ${active ? p.color : 'var(--rule-bright)'}`,
                borderLeft: `4px solid ${p.color}`,
                cursor: 'pointer',
              }}
            >
              {renaming === p.id ? (
                <>
                  <input
                    autoFocus
                    aria-label={`Name of ${p.name}`}
                    value={p.name}
                    onChange={(e) => onRenamePath(p.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') setRenaming(null);
                    }}
                    onBlur={() => setRenaming(null)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'var(--surface)',
                      border: '1px solid var(--rule-bright)',
                      padding: '2px 4px',
                      color: 'var(--text-pri)',
                      fontSize: '0.95rem',
                    }}
                  />
                  <IconButton label="Done renaming" onClick={() => setRenaming(null)}>
                    <Check size={13} />
                  </IconButton>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.95rem',
                        fontWeight: active ? 600 : 400,
                        color: active ? 'var(--text-pri)' : 'var(--text-sec)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.name}
                    </span>
                    {active && (
                      <span style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-ter)' }}>
                        Editing
                      </span>
                    )}
                  </span>
                  <IconButton label={`Rename ${p.name}`} onClick={() => setRenaming(p.id)}>
                    <Pencil size={12} />
                  </IconButton>
                  {paths.length > 1 && (
                    <IconButton label={`Remove ${p.name}`} onClick={() => onRemovePath(p.id)}>
                      <X size={13} />
                    </IconButton>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onAddPath}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 8px',
          marginTop: 8,
          cursor: 'pointer',
          fontSize: '0.9rem',
          color: 'var(--text-ter)',
          border: '1px dashed var(--rule-bright)',
          background: 'none',
          width: '100%',
        }}
      >
        + Add another path
      </button>
    </>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 2, display: 'flex' }}
    >
      {children}
    </button>
  );
}
